<template>
  <wa-dropdown
    placement="bottom-end"
    style="--link-decoration-line: none"
    distance="20"
    @wa-show="isOpen = true"
    @wa-hide="isOpen = false"
    @wa-after-show="onAfterShow"
    @wa-select="onSelect"
  >
    <!--
      Capture-phase listener: must run before vue-router's own click handler on the
      <site-link>'s <a> (same element), so preventDefault() here actually suppresses
      navigation. wa-dropdown's own trigger-click toggle (bubble-phase, on an ancestor)
      still fires afterward regardless, which is what opens/closes the menu.
    -->
    <span
      slot="trigger"
      @click.capture="onTriggerClick"
      @pointerdown="openedByPointer = true"
      @keydown="openedByPointer = false"
    >
      <site-link
        :to="routes.account.location"
        :class="['button flat', { active: routes.account.active }]"
        aria-label="Your Account"
      >
        <span
          class="display-none laptop:display-inline"
          style="font-weight: normal"
        >
          <span class="sr-only">Your </span>
          Account
        </span>
        <font-awesome-icon
          :icon="$v7.regular.faCircleUser"
          size="xl"
          class="margin-left-2xs"
        />
      </site-link>
    </span>

    <div
      class="padding-y-sm padding-x-lg"
      style="max-width: 20em"
    >
      <div
        v-if="fullName"
        class="text-truncate"
        style="font-weight: var(--font-weight-bold)"
        :title="fullName"
      >
        {{ fullName }}
      </div>
      <div
        class="size-sm text-truncate"
        :title="$account.email"
      >
        {{ $account.email }}
      </div>
    </div>

    <wa-divider />

    <wa-dropdown-item
      v-for="item in menuItems"
      :key="item.label"
      class="padding-y-sm padding-x-lg"
      :data-destination="item.destination"
      :style="item.highlight ? { backgroundColor: 'var(--blue1)' } : undefined"
    >
      <font-awesome-icon
        slot="icon"
        :icon="$v7.solid[item.icon]"
        size="lg"
      />
      {{ item.label }}
    </wa-dropdown-item>

    <wa-divider />

    <wa-dropdown-item
      class="padding-y-sm padding-x-lg"
      aria-label="Sign Out"
      data-destination="sign_out"
    >
      <font-awesome-icon
        slot="icon"
        :icon="$v7.solid.faArrowRightFromBracket"
        size="lg"
      />
      Sign Out
    </wa-dropdown-item>
  </wa-dropdown>
</template>

<script>
  import { mapActions } from 'vuex'
  import '@web.awesome.me/webawesome-pro/dist/components/dropdown/dropdown.js'
  import '@web.awesome.me/webawesome-pro/dist/components/dropdown-item/dropdown-item.js'
  import '@web.awesome.me/webawesome-pro/dist/components/divider/divider.js'
  import { determinations } from 'app/utils/subscription'
  import { PageHeaderRoutes } from './mixins'

  const ACTIVE_SUB_MENU = [
    { label: 'General', to: { name: 'account.general' }, icon: 'faBoxOpenFull', destination: 'account_general' },
    { label: 'Tokens', to: { name: 'account.tokens' }, icon: 'faCoinFront', destination: 'account_tokens' },
    { label: 'Team', to: { name: 'account.team' }, icon: 'faPeopleGroup', destination: 'account_team' },
    { label: 'Billing', to: { name: 'account.billing' }, icon: 'faReceipt', destination: 'account_billing' },
    { label: 'Settings', to: { name: 'account.settings' }, icon: 'faGear', destination: 'account_settings' }
  ]

  const FREE_MENU = [
    { label: 'General', to: { name: 'account.general' }, icon: 'faBoxOpenFull', destination: 'account_general' },
    { label: 'Tokens', to: { name: 'account.tokens' }, icon: 'faCoinFront', destination: 'account_tokens' },
    { label: 'Settings', to: { name: 'account.settings' }, icon: 'faGear', destination: 'account_settings' },
    { label: 'Upgrade', to: { name: 'plans' }, icon: 'faRocketLaunch', destination: 'account_upgrade', highlight: true }
  ]

  export default {
    name: 'PageHeaderAccountMenu',

    mixins: [PageHeaderRoutes],

    data() {
      return {
        isOpen: false,
        openedByPointer: false
      }
    },

    computed: {
      hasActivePaidSubscription() {
        return determinations({ subscription: this.$account.subscription }).hasActivePaidSubscription
      },

      menuItems() {
        return this.hasActivePaidSubscription ? ACTIVE_SUB_MENU : FREE_MENU
      },

      fullName() {
        return [this.$profile?.firstName, this.$profile?.lastName].filter(Boolean).join(' ')
      }
    },

    methods: {
      ...mapActions('sessions', ['signOut']),

      // wa-dropdown auto-focuses the first item on open; Safari paints that as a
      // keyboard focus ring even for mouse opens, so blur it unless opened via keyboard.
      onAfterShow() {
        if (this.openedByPointer) {
          this.$el.querySelector('wa-dropdown-item:focus')?.blur()
        }
      },

      onTriggerClick(event) {
        if (!this.isOpen) {
          event.preventDefault()
        } else {
          this.$posthog.capture('main_navigation_link_click', { destination: 'account_general' })
        }
      },

      onSelect(event) {
        const { destination } = event.detail.item.dataset

        if (destination === 'sign_out') {
          this.beginSignOut()
          return
        }

        const item = this.menuItems.find((menuItem) => menuItem.destination === destination)
        this.$posthog.capture('main_navigation_link_click', { destination: item.destination })
        this.$router.push(item.to)
      },

      async beginSignOut() {
        this.$posthog.capture('sign_out_button_click')

        await this.signOut()

        this.$posthog.capture('sign_out_end')
        this.$router.push({ name: 'sessions.sign-in' })
      }
    }
  }
</script>
